import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import api, { fileUrl, formatApiError } from "../lib/api";
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, ResponsiveContainer } from "recharts";
import { ShieldCheck, MapPin, Star, Share2, Download } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { generatePlayerCard } from "../lib/cardGenerator";
import { useTranslation } from "react-i18next";

const RADAR_TICK = { fill: "#eab308", fontSize: 12, fontFamily: "Oswald" };

export default function PlayerProfile() {
  const { id } = useParams();
  const { t } = useTranslation();
  const [p, setP] = useState(null);

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get(`/players/${id}`); setP(data); }
      catch (e) { toast.error(formatApiError(e)); }
    })();
  }, [id]);

  // Track a view (best-effort, throttled server-side to 1/IP/30min).
  // Fire once per browser session per player to reduce noise.
  useEffect(() => {
    if (!id) return;
    const key = `afri_viewed_${id}`;
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, "1");
    api.post(`/players/${id}/view`).catch((err) => console.debug("view track failed", err?.message));
  }, [id]);

  useEffect(() => {
    if (p?.name) {
      document.title = `${p.name} · ${p.position || "Player"} · AfriGoall`;
      let m = document.querySelector('meta[name="description"]');
      if (!m) { m = document.createElement("meta"); m.name = "description"; document.head.appendChild(m); }
      m.content = `${p.name} — ${p.position || "Player"} from ${p.country || "Africa"}. Verified profile on AfriGoall. ${p.age ? p.age + " years old, " : ""}${p.height_cm ? p.height_cm + "cm." : ""}`;
    }
  }, [p]);

  if (!p) return <div className="min-h-screen bg-black text-white grain"><Navbar /><div className="p-10 text-center">{t("common.loading")}</div></div>;

  const downloadCard = async () => {
    try {
      const blob = await generatePlayerCard(p);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `${(p.name || "player").replace(/\s+/g, "_")}_afrigoal.png`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(t("profile.downloadCard") + " ✓");
    } catch { toast.error(t("toast.cardFailed")); }
  };

  const shareCard = async () => {
    try {
      const blob = await generatePlayerCard(p);
      const file = new File([blob], `${p.name}_afrigoal.png`, { type: "image/png" });
      if (navigator.share && navigator.canShare?.({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: t("toast.shareTitle", { name: p.name }),
          text: t("toast.shareText", {
            name: p.name,
            position: p.position || t("toast.playerFallback"),
            country: p.country || t("toast.africaFallback"),
          }),
        });
      } else {
        const url = URL.createObjectURL(blob);
        window.open(url, "_blank");
        toast.success(t("profile.shareCard") + " ✓");
      }
    } catch (e) {
      if (e?.name !== "AbortError") toast.error(t("toast.shareUnsupported"));
    }
  };

  const radar = [
    { attr: t("radar.Speed"), value: Math.min(100, (p.speed_kmh || 0) * 3) },
    { attr: t("radar.Stamina"), value: p.stamina || 0 },
    { attr: t("radar.Strength"), value: p.strength || 0 },
    { attr: t("radar.Dribble"), value: p.dribbling || 0 },
    { attr: t("radar.Shoot"), value: p.shooting || 0 },
    { attr: t("radar.Pass"), value: p.passing || 0 },
    { attr: t("radar.Defend"), value: p.defending || 0 },
  ];

  return (
    <div className="min-h-screen bg-black text-white grain" data-testid="player-profile-page">
      <Navbar />
      <div className="relative">
        <div className="h-[45vh] w-full bg-black relative overflow-hidden">
          {p.photo_path
            ? <img src={fileUrl(p.photo_path)} alt={p.name} className="w-full h-full object-cover object-top opacity-70" />
            : <div className="w-full h-full bg-gradient-to-br from-[#1a1a1a] to-black" />}
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
          <div className="absolute bottom-8 left-0 right-0 max-w-7xl mx-auto px-6">
            {p.verified && (
              <div className="inline-flex items-center gap-1 bg-[#eab308] text-black px-3 py-1 text-xs font-bold uppercase tracking-widest mb-3">
                <ShieldCheck size={14} /> {t("common.verified")}
              </div>
            )}
            <h1 className="font-display text-5xl md:text-7xl font-bold uppercase tracking-tighter flex items-center gap-3 flex-wrap">
              {p.name}
              {p.verified && (
                <span
                  className="inline-flex items-center justify-center w-9 h-9 md:w-11 md:h-11 rounded-full bg-[#eab308] text-black shrink-0 shadow-[0_0_16px_rgba(234,179,8,0.5)]"
                  data-testid="verified-badge-name"
                  title={t("profile.verifiedTooltip")}
                >
                  <ShieldCheck size={20} strokeWidth={3} />
                </span>
              )}
            </h1>
            <div className="mt-2 text-white/70 flex flex-wrap items-center gap-4 text-sm">
              <span className="flex items-center gap-1"><MapPin size={14} />{p.country || "—"}</span>
              <span className="uppercase tracking-widest">{p.nationality}</span>
              <span className="uppercase tracking-widest text-[#e65f2b]">{p.position || "—"}</span>
            </div>
            <div className="mt-4 flex flex-wrap gap-3">
              <Button
                onClick={shareCard}
                data-testid="share-card-btn"
                className="bg-[#e65f2b] hover:bg-[#c54a1c] text-black font-bold uppercase tracking-widest rounded-sm h-10"
              >
                <Share2 size={14} className="mr-2" />{t("profile.shareCard")}
              </Button>
              <Button
                onClick={downloadCard}
                data-testid="download-card-btn"
                variant="outline"
                className="border-white/30 text-white hover:bg-white/10 rounded-sm h-10 uppercase tracking-widest"
              >
                <Download size={14} className="mr-2" />{t("profile.downloadCard")}
              </Button>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-6 py-10 grid md:grid-cols-12 gap-6">
          <div className="md:col-span-4 border border-white/10 bg-[#141414] p-6">
            <div className="text-xs uppercase tracking-widest text-white/60 mb-4">{t("profile.vitals")}</div>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <Stat label={t("common.age")} value={p.age} />
              <Stat label={t("profile.dominantFoot")} value={p.dominant_foot} />
              <Stat label={t("profile.height")} value={p.height_cm ? `${p.height_cm} cm` : "—"} />
              <Stat label={t("profile.weight")} value={p.weight_kg ? `${p.weight_kg} kg` : "—"} />
              <Stat label={t("profile.speed")} value={p.speed_kmh ? `${p.speed_kmh} km/h` : "—"} />
              <Stat label={t("profile.passport")} value={p.passport_status} />
              <Stat label={t("profile.academy")} value={p.academy} />
              <Stat label={t("common.city")} value={p.city} />
            </div>
            {p.bio && (
              <div className="mt-6">
                <div className="text-xs uppercase tracking-widest text-white/60 mb-2">{t("profile.bio")}</div>
                <p className="text-sm text-white/80">{p.bio}</p>
              </div>
            )}
          </div>

          <div className="md:col-span-8 border border-white/10 bg-[#141414] p-6">
            <div className="text-xs uppercase tracking-widest text-white/60 mb-4">{t("profile.radar")}</div>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart data={radar}>
                  <PolarGrid stroke="#333" />
                  <PolarAngleAxis dataKey="attr" tick={RADAR_TICK} />
                  <Radar dataKey="value" stroke="#e65f2b" fill="#e65f2b" fillOpacity={0.4} />
                </RadarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="md:col-span-12">
            <div className="text-xs uppercase tracking-widest text-white/60 mb-3">{t("profile.highlights")}</div>
            <div className="grid md:grid-cols-3 gap-4">
              {(p.videos || []).map((v) => (
                <div key={v.id} className="border border-white/10 bg-[#141414]" data-testid={`profile-video-${v.id}`}>
                  <video controls src={fileUrl(v.storage_path)} poster={v.thumbnail_path ? fileUrl(v.thumbnail_path) : undefined} className="w-full aspect-video bg-black" />
                  <div className="p-4">
                    <div className="font-display uppercase font-bold">{v.title}</div>
                    <div className="text-xs text-white/50 uppercase tracking-widest">{v.category}</div>
                  </div>
                </div>
              ))}
              {(p.videos || []).length === 0 && <div className="text-white/50">{t("profile.noHighlights")}</div>}
            </div>
          </div>

          {p.ratings && p.ratings.length > 0 && (
            <div className="md:col-span-12">
              <div className="text-xs uppercase tracking-widest text-white/60 mb-3">{t("profile.reports")}</div>
              <div className="grid md:grid-cols-2 gap-4">
                {p.ratings.map((r) => (
                  <div key={r.id} className="border border-white/10 bg-[#141414] p-5" data-testid={`profile-rating-${r.id}`}>
                    <div className="flex items-center justify-between">
                      <div className="font-bold">{r.rater_name} <span className="text-xs uppercase text-white/50">· {r.rater_role}</span></div>
                      <div className="flex items-center gap-1 text-[#eab308] font-display text-xl">
                        <Star size={16} fill="currentColor" />{r.rating}/10
                      </div>
                    </div>
                    <p className="mt-2 text-sm text-white/80">{r.report}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-white/50">{label}</div>
      <div className="mt-1 font-display text-lg font-bold">{value || "—"}</div>
    </div>
  );
}
